import {
  Checkbox,
  Grid,
  Icon,
  Input,
  Popup,
  Ref,
  Select,
} from "semantic-ui-react";
import FancyHeader from "../../../../components/shared/FancyHeader";
import { useRef } from "react";
import {
  getFullRegionName,
  renderZonesForSelectBox,
} from "../../../../app_shared_functions";
import useNetworkDuplicateForm from "./hooks/useNetworkDuplicateForm";
import { formFieldWarningClassName } from "../../../../shared-functions/form";
import { useSelector } from "react-redux";
import { getDomains, getProjectsObject } from "../../../../selectors/projects";
import Expandable from "../../../../components/shared/Expandable";
import useZoneId from "../../../../custom-hooks/region/useZoneId";
import MTUStatus from "../create/bits/MTUStatus";
import { PredefinedParams } from "../../../../components/slidingpanel/type";
import { safeToLowerCase } from "../../../../shared-functions/string";
import useDuplicateClickErrorHandler from "./hooks/useDuplicateClickErrorHandler";
import useNetworkDuplicateHandler from "./hooks/useDuplicateNetworkAction";

type NetworkDuplicatorProps = {
  predefined_params?: PredefinedParams;
  closeSlidingMenuLayer: () => void;
};

const NetworkDuplicator = ({
  predefined_params,
  closeSlidingMenuLayer,
}: NetworkDuplicatorProps) => {
  const regionRef = useRef<HTMLElement | null>(null);

  const domains = useSelector(getDomains);
  const projects = useSelector(getProjectsObject);
  const areasList = renderZonesForSelectBox(projects, domains);

  const {
    error,
    formData,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    formWarning,
    showFormWarning,
  } = useNetworkDuplicateForm({
    regionRef,
  });

  const zoneId = useZoneId(formData?.region || predefined_params?.region);

  const onDisabledDuplicateClick = useDuplicateClickErrorHandler({
    error,
    showFormWarning,
  });

  const { duplicateNetwork, isCreating } = useNetworkDuplicateHandler({
    formData,
    predefined_params,
  });

  return (
    <div className="creator-component-wrapper">
      <FancyHeader title="Duplicate Network" knowledgeBase />
      <p />

      <Grid>
        {/* NAME - REGION */}
        <Grid.Row className="separator padding-top-10">
          <Grid.Column
            textAlign="left"
            width={16}
            className="flex vcenter margin-bottom-20"
          >
            Please choose the region to duplicate to.
          </Grid.Column>

          <Grid.Column textAlign="left" width={8} className="flex vcenter ">
            <h5>
              Name
              <Popup
                trigger={
                  <Icon
                    name="warning circle"
                    color="grey"
                    size="small"
                    className="margin-left-10"
                  />
                }
              >
                You can enter a new name for the duplicated network. If this
                field is left empty, the source name will be set automatically.
              </Popup>
            </h5>
          </Grid.Column>
          <Grid.Column textAlign="left" width={8}>
            <Input
              placeholder={predefined_params?.name}
              className="select-box full"
              value={formData.name}
              name="name"
              onChange={handleInputChange}
            />
          </Grid.Column>

          <Grid.Column
            textAlign="left"
            width={8}
            className="flex vcenter margin-top-30"
          >
            <h5>Region</h5>
          </Grid.Column>
          <Grid.Column
            textAlign="left"
            width={8}
            className="flex vcenter margin-top-30"
          >
            <Ref innerRef={regionRef}>
              <Select
                icon="chevron circle down"
                className={`select-box full ${formFieldWarningClassName(
                  formWarning,
                  error?.ref,
                  regionRef,
                )}`}
                placeholder={
                  predefined_params?.zone
                    ? getFullRegionName(
                        domains,
                        safeToLowerCase(predefined_params.region),
                      )
                    : "Choose Region"
                }
                options={areasList}
                name="region"
                onChange={handleSelectChange}
              />
            </Ref>
          </Grid.Column>
        </Grid.Row>

        {/* ADVANCED */}
        {formData.region || predefined_params?.region ? (
          <>
            <Expandable defaultOpened={false} title="Advanced Options">
              <Grid.Row>
                <MTUStatus
                  zoneId={zoneId}
                  value={formData.mtu}
                  onChange={handleInputChange}
                />
                <Grid.Column
                  textAlign="left"
                  width={8}
                  className="margin-top-20 flex vbottom"
                >
                  <h5>Port Security</h5>
                </Grid.Column>

                <Grid.Column
                  textAlign="left"
                  width={8}
                  className="margin-top-20"
                >
                  <Checkbox
                    className="display-inlineblock"
                    name="portSecurity"
                    toggle
                    checked={formData.portSecurity}
                    label={formData.portSecurity ? "Enabled" : "Disabled"}
                    onChange={handleCheckboxChange}
                  />
                </Grid.Column>
              </Grid.Row>
            </Expandable>
            <Grid.Row className="separator padding-top-00"></Grid.Row>
          </>
        ) : null}

        <Grid.Row className="padding-top-00">
          <Grid.Column textAlign="left" width={16}>
            {error ? (
              <Popup
                trigger={
                  <button
                    className="float-right button button--green button--disabled button--icon__left"
                    onClick={onDisabledDuplicateClick}
                  >
                    <Icon name="exclamation circle" />
                    <span>Duplicate</span>
                  </button>
                }
              >
                {error?.message}
              </Popup>
            ) : isCreating ? (
              <button className="float-right button button--green overflow-hidden button--icon__right">
                <Icon loading name="spinner" />
                <span>Duplicating</span>
              </button>
            ) : (
              <button
                className="float-right button button--green"
                onClick={duplicateNetwork}
              >
                <span>Duplicate</span>
              </button>
            )}

            <button
              className="button button--bordered"
              onClick={closeSlidingMenuLayer}
            >
              <span>Back</span>
            </button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

export default NetworkDuplicator;
